import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import * as Hammer from 'hammerjs';

enum KeyboardKey {
  ARROWUP = 'ArrowUp',
  ARROWDOWN = 'ArrowDown',
  ARROWLEFT = 'ArrowLeft',
  ARROWRIGHT = 'ArrowRight',
}

export interface Options {
  floor?: number;
  ceil?: number;
  step?: number;
  minValue?: number;
  value?: number;
  isRtl?: boolean;
  labelPosition?: 'top' | 'bottom';
  minValueLabel?: (minValue: number) => string;
  valueLabel?: (value: number) => string;
  minValueAriaLabel?: (minValue: number) => string;
  valueAriaLabel?: (value: number) => string;
}

@Component({
  selector: 'ascenda-ngx-slider',
  templateUrl: './ngx-slider.component.html',
  styleUrls: ['./ngx-slider.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NgxSliderComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @Input() options!: Options;
  @Output() minValueChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() valueChange: EventEmitter<number> = new EventEmitter<number>();
  @ViewChild('slider') sliderElement!: ElementRef;
  @ViewChild('minButton') minButtonElement!: ElementRef;
  @ViewChild('maxButton') maxButtonElement!: ElementRef;

  floor!: number;
  ceil!: number;
  step!: number;
  minValue!: number;
  maxValue!: number;
  isRange!: boolean;
  isRtl!: boolean;
  labelPosition!: 'top' | 'bottom';
  labelStyles!: { [klass: string]: string };
  valueDistance!: number;
  minValueLabel!: (minValue: number) => string;
  valueLabel!: (value: number) => string;
  minValueAriaLabel!: (minValue: number) => string;
  valueAriaLabel!: (value: number) => string;
  minPointerMarginStyles!: string;
  maxPointerMarginStyles!: string;

  private sliderDistance!: number;
  private sliderDistanceToWindowEdge!: number;
  private movedSteps!: number;
  private precision!: number;
  private minButtonHammerManager!: HammerManager;
  private maxButtonHammerManager!: HammerManager;
  private emittedMinValue!: number;
  private emittedMaxValue!: number;
  private windowResizeTimeout?: number;

  constructor(private cdr: ChangeDetectorRef) {}

  @HostListener('window:resize')
  handleWindowResize(): void {
    // call initSliderViewAndEvents after 100ms of window resizing finish
    window.clearTimeout(this.windowResizeTimeout);
    this.windowResizeTimeout = window.setTimeout(() => {
      this.initSliderViewAndEvents();
    }, 100);
  }

  ngOnInit() {
    this.initSliderConfig();
  }

  ngAfterViewInit() {
    this.initSliderViewAndEvents();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.options && !changes.options.firstChange) {
      this.initSliderConfig();
      this.initSliderViewAndEvents();
    }
  }

  ngOnDestroy() {
    this.destroyPanEvents();
  }

  getOffsetStyles(value: number): { [klass: string]: string } {
    const offset = `${this.convertValueToPixels(value - this.floor)}px`;
    return this.isRtl ? { right: offset } : { left: offset };
  }

  startPan(): void {
    this.movedSteps = 0;
  }

  clickOnSlider(event: MouseEvent): void {
    const clickedPos: number = this.isRtl
      ? this.sliderDistanceToWindowEdge - event.clientX
      : event.clientX - this.sliderDistanceToWindowEdge;
    const clickedPosOffsetToMin: number =
      clickedPos - this.convertValueToPixels(this.minValue - this.floor);
    const clickedPosOffsetToMax: number =
      clickedPos - this.convertValueToPixels(this.maxValue - this.floor);
    if (
      this.isRange &&
      Math.abs(clickedPosOffsetToMin) <= Math.abs(clickedPosOffsetToMax)
    ) {
      this.startPan();
      this.slideMin(clickedPosOffsetToMin);
      this.emitMinValue();
    } else {
      this.startPan();
      this.slideMax(clickedPosOffsetToMax);
      this.emitMaxValue();
    }
  }

  slideMin(movedPixels: number): void {
    const additionalStepsToMove: number =
      this.calculateAdditionalStepsToMove(movedPixels);
    if (additionalStepsToMove === 0) {
      return;
    }

    this.updateMinValueBySteps(additionalStepsToMove);
    this.cdr.detectChanges();
  }

  slideMax(movedPixels: number): void {
    const additionalStepsToMove: number =
      this.calculateAdditionalStepsToMove(movedPixels);
    if (additionalStepsToMove === 0) {
      return;
    }

    this.updateMaxValueBySteps(additionalStepsToMove);
    this.cdr.detectChanges();
  }

  keyupMin(event: KeyboardEvent): void {
    if (
      event.key === KeyboardKey.ARROWUP ||
      event.key === KeyboardKey.ARROWRIGHT
    ) {
      this.updateMinValueBySteps(this.isRtl ? -1 : 1);
      this.emitMinValue();
    } else if (
      event.key === KeyboardKey.ARROWDOWN ||
      event.key === KeyboardKey.ARROWLEFT
    ) {
      this.updateMinValueBySteps(this.isRtl ? 1 : -1);
      this.emitMinValue();
    }
  }

  keyupMax(event: KeyboardEvent): void {
    if (
      event.key === KeyboardKey.ARROWUP ||
      event.key === KeyboardKey.ARROWRIGHT
    ) {
      this.updateMaxValueBySteps(this.isRtl ? -1 : 1);
      this.emitMaxValue();
    } else if (
      event.key === KeyboardKey.ARROWDOWN ||
      event.key === KeyboardKey.ARROWLEFT
    ) {
      this.updateMaxValueBySteps(this.isRtl ? 1 : -1);
      this.emitMaxValue();
    }
  }

  updateMinValueBySteps(numberOfSteps: number): void {
    let minValue: number = this.minValue + numberOfSteps * this.step;
    minValue = +minValue.toFixed(this.precision); // round to precision
    if (minValue < this.floor) {
      minValue = this.floor;
    }
    if (minValue > this.maxValue) {
      minValue = this.maxValue;
    }
    this.movedSteps = (minValue - this.minValue) / this.step + this.movedSteps;

    this.minValue = minValue;
  }

  updateMaxValueBySteps(numberOfSteps: number): void {
    let maxValue: number = this.maxValue + numberOfSteps * this.step;
    maxValue = +maxValue.toFixed(this.precision); // round to precision
    if (maxValue > this.ceil) {
      maxValue = this.ceil;
    }
    if (maxValue < this.minValue) {
      maxValue = this.minValue;
    }
    this.movedSteps = (maxValue - this.maxValue) / this.step + this.movedSteps;

    this.maxValue = maxValue;
  }

  convertValueToPixels(value: number): number {
    return (value * this.sliderDistance) / this.valueDistance;
  }

  private convertPixelsToValue(pixels: number): number {
    return (pixels * this.valueDistance) / this.sliderDistance;
  }

  private calculateAdditionalStepsToMove(movedPixels: number): number {
    const movedValue: number = this.convertPixelsToValue(movedPixels);
    const movedValueInStep: number = Math.round(movedValue / this.step);
    return movedValueInStep - this.movedSteps;
  }

  private initSliderConfig(): void {
    this.options = this.options || {};
    this.isRange = this.options.minValue != null;
    this.isRtl = this.options.isRtl || false;
    this.determinePrecision();
    this.buildFloorCeilValues();
    this.buildStepValue();
    this.buildMinMaxValues();
    this.buildLabelStyles();
    this.buildLabelFormatter();
    this.buildAriaLabelFormatter();
  }

  private initSliderViewAndEvents(): void {
    this.checkSliderWidth();
    this.buildPointerMarginStyles();
    this.initPanEvents();
    this.cdr.detectChanges();
  }

  private checkSliderWidth(): void {
    this.sliderDistance = this.sliderElement.nativeElement.clientWidth;
    this.sliderDistanceToWindowEdge = this.isRtl
      ? this.sliderElement.nativeElement.getBoundingClientRect().right
      : this.sliderElement.nativeElement.getBoundingClientRect().left;
  }

  private determinePrecision(): void {
    this.precision = Math.max(
      this.countNumberOfDecimalPlaces(this.options.floor),
      this.countNumberOfDecimalPlaces(this.options.ceil),
      this.countNumberOfDecimalPlaces(this.options.step),
    );
  }

  private buildFloorCeilValues(): void {
    // null check
    const floor: number = this.options.floor ?? 0;
    const ceil: number = this.options.ceil ?? 100;
    // round to precision
    this.floor = +floor.toFixed(this.precision);
    this.ceil = +ceil.toFixed(this.precision);
    // determine slider value offset
    this.valueDistance = this.ceil - this.floor;
    if (this.valueDistance < 0) {
      throw new Error('floor should not be greater than ceil');
    }
  }

  private buildStepValue(): void {
    // null check
    const step: number = this.options.step ? Math.abs(this.options.step) : 1;
    // round to precision
    this.step = +step.toFixed(this.precision);
  }

  private buildMinMaxValues(): void {
    // null check
    let minValue: number = this.options.minValue ?? this.floor;
    let maxValue: number =
      this.options.value ??
      (this.options.minValue == null ? this.floor : this.ceil);
    // round to closest step value
    minValue = Math.round(minValue / this.step) * this.step;
    maxValue = Math.round(maxValue / this.step) * this.step;
    // round to precision
    minValue = +minValue.toFixed(this.precision);
    maxValue = +maxValue.toFixed(this.precision);
    // range check
    minValue = minValue < this.floor ? this.floor : minValue;
    minValue = minValue > this.ceil ? this.ceil : minValue;
    maxValue = maxValue < this.floor ? this.floor : maxValue;
    maxValue = maxValue > this.ceil ? this.ceil : maxValue;
    // restore minValue to ceil if not range slider, otherwise prevent min being greater than max
    minValue = this.isRange ? Math.min(minValue, maxValue) : this.floor;

    this.minValue = minValue;
    this.maxValue = maxValue;
    this.emitMinValue();
    this.emitMaxValue();
  }

  private buildLabelStyles(): void {
    if (this.options.labelPosition === 'bottom') {
      this.labelStyles = { position: 'absolute', bottom: '-25px' };
    } else {
      this.labelStyles = { position: 'absolute', top: '-25px' };
    }
  }

  private buildLabelFormatter(): void {
    this.minValueLabel =
      this.options.minValueLabel ||
      ((minValue: number): string => `${minValue}`);
    this.valueLabel =
      this.options.valueLabel || ((value: number): string => `${value}`);
  }

  private buildAriaLabelFormatter(): void {
    this.minValueAriaLabel =
      this.options.minValueAriaLabel ||
      ((minValue: number): string => {
        return `Slider minimum value pointer of value ${minValue}`;
      });

    this.valueAriaLabel =
      this.options.valueAriaLabel ||
      ((value: number): string => {
        if (this.isRange) {
          return `Slider maximum value pointer of value ${value}`;
        }
        return `Slider pointer of value ${value}`;
      });
  }

  /**
   * Build margin styles for pointers based on pointer's height and width on consumer app.
   * See `formatPointerMarginStyles` for calculation details
   */
  private buildPointerMarginStyles(): void {
    const maxPointerHeight: number =
      this.maxButtonElement.nativeElement.clientHeight;
    const maxPointerWidth: number =
      this.maxButtonElement.nativeElement.clientWidth;
    this.maxPointerMarginStyles = this.formatPointerMarginStyles(
      maxPointerHeight,
      maxPointerWidth,
    );
    if (this.isRange) {
      const minPointerHeight: number =
        this.minButtonElement.nativeElement.clientHeight;
      const minPointerWidth: number =
        this.minButtonElement.nativeElement.clientWidth;
      this.minPointerMarginStyles = this.formatPointerMarginStyles(
        minPointerHeight,
        minPointerWidth,
      );
    }
  }

  /**
   * If pointer's height is 20px, width is 18px, and slider bar is 4px height,
   * it will return `-8px 0 0 -9px` for ltr and `-8px -9px 0 0` for rtl,
   * so that the pointers will be positioned at the center
   */
  private formatPointerMarginStyles(
    pointerHeight: number,
    pointerWidth: number,
  ): string {
    const sliderBarHeight: number =
      this.sliderElement.nativeElement.clientHeight;
    return `
      -${(pointerHeight - sliderBarHeight) / 2}px
      ${this.isRtl ? -pointerWidth / 2 + 'px' : 0}
      0
      ${this.isRtl ? 0 : -pointerWidth / 2 + 'px'}
    `;
  }

  private initPanEvents(): void {
    this.destroyPanEvents();

    if (this.isRange) {
      this.minButtonHammerManager = new Hammer(
        this.minButtonElement.nativeElement,
      );
      this.minButtonHammerManager
        .get('pan')
        .set({ threshold: 1, direction: 6 });
      this.minButtonHammerManager.on('panstart', () => this.startPan());
      this.minButtonHammerManager.on('panleft panright', (ev: HammerInput) => {
        this.slideMin(this.isRtl ? -ev.deltaX : ev.deltaX);
      });
      this.minButtonHammerManager.on('panend', () => this.emitMinValue());
    }
    this.maxButtonHammerManager = new Hammer(
      this.maxButtonElement.nativeElement,
    );
    this.maxButtonHammerManager.get('pan').set({ threshold: 1, direction: 6 });
    this.maxButtonHammerManager.on('panstart', () => this.startPan());
    this.maxButtonHammerManager.on('panleft panright', (ev: HammerInput) => {
      this.slideMax(this.isRtl ? -ev.deltaX : ev.deltaX);
    });
    this.maxButtonHammerManager.on('panend', () => this.emitMaxValue());
  }

  private destroyPanEvents(): void {
    this.minButtonHammerManager?.destroy();
    this.maxButtonHammerManager?.destroy();
  }

  private emitMinValue(): void {
    if (!this.isRange || this.minValue === this.emittedMinValue) {
      return;
    }

    this.emittedMinValue = this.minValue;
    this.minValueChange.emit(this.minValue);
  }

  private emitMaxValue(): void {
    if (this.maxValue === this.emittedMaxValue) {
      return;
    }

    this.emittedMaxValue = this.maxValue;
    this.valueChange.emit(this.maxValue);
  }

  private countNumberOfDecimalPlaces(val: number | undefined): number {
    if (!val || val % 1 === 0) {
      return 0;
    }
    const decimalPart: string = val.toString().split('.')[1];
    return decimalPart ? decimalPart.length : 0;
  }
}
